import React, { ChangeEvent } from 'react';
import {
  Select, TextInput, Label,
} from 'nhsuk-react-components';
import { IRuleState, RuleActions } from 'reducers/rule/ruleReducer';
import { ActionTypes } from 'components/forms/Rule/Editors/ActionTypes';
import { getCohortStatus } from 'utils/cohortUtils.ts';
import { capitaliseFirstChar } from 'utils/formattingUtils.ts';
import { CohortMetadata } from 'schemas/metadata/cohorts.ts';
import CustomReactSelect, { Option } from 'components/CustomReactSelect/CustomReactSelect';

export interface ComparatorControlProps {
  ruleState: IRuleState;
  dispatch: React.Dispatch<RuleActions>;
  handleTextChange: (e: ChangeEvent<HTMLInputElement>, actionType: ActionTypes) => void;
  errors: { [key: string]: string };
  isComparatorRequired: () => boolean;
  teamCohorts: CohortMetadata[];
}

type CohortSelectProps = Pick<ComparatorControlProps, 'ruleState' | 'dispatch' | 'teamCohorts'>;

function getCohortStatusLabel(cohortLabel: string, teamCohorts: CohortMetadata[]): string {
  const cohortStatus = getCohortStatus(cohortLabel, teamCohorts);
  return cohortStatus === 'ok' ? cohortLabel : `${cohortLabel} (${capitaliseFirstChar(cohortStatus)})`;
}

function CohortMultiSelect({ ruleState, dispatch, teamCohorts }: CohortSelectProps) {
  const handleChange = (selectedOptions: Option[] | null): void => {
    const selectedValues = selectedOptions?.map((option) => option.value) ?? [];
    dispatch({
      type: 'SET_COMPARATOR',
      payload: selectedValues.join(','),
    });
  };

  const sortedTeamCohorts = teamCohorts.toSorted((a, b) => a.CohortLabel.localeCompare(b.CohortLabel));

  return (
    <div>
      <Label><b>Choose Cohort</b></Label>
      <CustomReactSelect
        isMulti
        inputId="rule-comparator"
        options={sortedTeamCohorts.map((cohort: { CohortLabel: string }) => ({
          value: cohort.CohortLabel,
          label: getCohortStatusLabel(cohort.CohortLabel, teamCohorts),
        }))}
        onChange={handleChange}
        placeholder="Select..."
        value={ruleState.selectedComparator
          .split(',')
          .map((value) => value.trim())
          .filter((value) => value !== '')
          .map((value) => ({
            value,
            label: getCohortStatusLabel(value, teamCohorts),
          }))}
      />
    </div>
  );
}

function CohortSingleSelect({ ruleState, dispatch, teamCohorts }: CohortSelectProps) {
  return (
    <Select
      id="rule-comparator"
      label="Choose Cohort"
      onChange={(e: ChangeEvent<HTMLSelectElement>) => dispatch({
        type: 'SET_COMPARATOR',
        payload: e.target.value,
      })}
      value={ruleState.selectedComparator}
    >
      {teamCohorts.map((cohort: { CohortLabel: string }) => (
        <Select.Option
          key={cohort.CohortLabel}
          value={cohort.CohortLabel}
        >
          {getCohortStatusLabel(cohort.CohortLabel, teamCohorts)}
        </Select.Option>
      ))}
    </Select>
  );
}

function ComparatorControl({
  ruleState,
  dispatch,
  handleTextChange,
  errors,
  isComparatorRequired,
  teamCohorts,
}: ComparatorControlProps) {
  if (ruleState.selectedAttributeLevel !== 'COHORT') {
    return (
      <TextInput
        id="rule-comparator"
        label="Choose comparator"
        name="Comparator"
        onChange={(e: ChangeEvent<HTMLInputElement>) => handleTextChange(e, 'SET_COMPARATOR')}
        value={ruleState.selectedComparator}
        error={errors.Comparator}
        disabled={!isComparatorRequired()}
      />
    );
  }

  if (ruleState.selectedOperator.OperatorType === 'M') {
    return <CohortMultiSelect ruleState={ruleState} dispatch={dispatch} teamCohorts={teamCohorts} />;
  }

  return <CohortSingleSelect ruleState={ruleState} dispatch={dispatch} teamCohorts={teamCohorts} />;
}

export default ComparatorControl;
